import { useCallback, useEffect, useMemo, useState } from 'react';
import { getStudentDirectory, getDirectoryFilters, sendBulkParentInvites, assetUrl } from '../../services/api';
import { getRumaalColor, getStudentStatusColor } from '../../utils/colors';
import StudentEditor from './StudentEditor';
import AddStudent from './AddStudent';
import EmailImport from './EmailImport';

const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm transition-shadow duration-150 outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';
const PAGE_SIZE = 50;

const PORTAL_LABELS = {
  activated: { text: 'Signed in', className: 'bg-green-100 text-green-700' },
  invited: { text: 'Invited', className: 'bg-purple-100 text-purple-700' },
  'not-invited': { text: 'Not invited', className: 'bg-amber-100 text-amber-700' },
  'no-email': { text: 'No email', className: 'bg-gray-100 text-gray-600' },
};

const EMPTY_FILTERS = {
  search: '', rank: '', instructor: '', classId: '',
  division: '', gender: '', status: '', portalStatus: '',
};

function Select({ label, value, onChange, options, allLabel }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={FIELD} aria-label={label}>
      <option value="">{allLabel}</option>
      {options.map((option) => {
        const key = typeof option === 'string' ? option : option.id;
        const text = typeof option === 'string' ? option : option.label;
        return <option key={key} value={key}>{text}</option>;
      })}
    </select>
  );
}

function StudentRow({ student, onOpen }) {
  const portal = PORTAL_LABELS[student.portalStatus] || PORTAL_LABELS['no-email'];
  const initials = student.displayName.split(' ').map((w) => w[0]).slice(0, 2).join('');

  return (
    <button
      onClick={() => onOpen(student)}
      className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-all hover:border-gray-300 hover:shadow-sm"
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
        // The rank ring, drawn as a box-shadow rather than Tailwind's ring
        // utility so the colour is set directly and can't depend on a theme
        // variable name.
        style={{ boxShadow: `0 0 0 2px ${getRumaalColor(student.rumaalRank)}` }}
      >
        {student.photoUrl
          ? <img src={assetUrl(student.photoUrl)} alt="" className="h-full w-full object-cover" />
          : <span className="text-xs font-semibold text-gray-400">{initials}</span>}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-gray-900">{student.displayName}</span>
          {student.isBirthdayToday && <span title="Birthday today">🎂</span>}
        </div>
        <div className="truncate text-xs text-gray-500">
          {student.rosterEntries?.length
            ? student.rosterEntries.map((e) => `${e.classLabel} · ${e.instructor}`).join('  |  ')
            : 'Not in any class'}
        </div>
      </div>

      <div className="hidden shrink-0 items-center gap-2 sm:flex">
        {student.ageDivision && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {student.ageDivision}
          </span>
        )}
        {student.status && (
          <span
            className="rounded-full px-2 py-0.5 text-xs font-medium text-gray-700"
            style={{ backgroundColor: getStudentStatusColor(student.status) }}
          >
            {student.status}
          </span>
        )}
        {student.rumaalRank && (
          <span
            className="rounded-full px-2 py-0.5 text-xs font-medium"
            style={{
              backgroundColor: getRumaalColor(student.rumaalRank),
              color: student.rumaalRank === 'White' || student.rumaalRank === 'Yellow' ? '#000' : '#fff',
            }}
          >
            {student.rumaalRank}
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${portal.className}`}>
          {portal.text}
        </span>
      </div>
    </button>
  );
}

export default function StudentDirectory() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ students: [], total: 0, summary: null });
  const [options, setOptions] = useState({ instructors: [], ranks: [], statuses: [], divisions: [], genders: [], classes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  // Typing in the search box shouldn't fire a request per keystroke against a
  // 450-student join.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 250);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: result } = await getStudentDirectory({
        ...filters, search: debouncedSearch, sort, page, limit: PAGE_SIZE,
      });
      setData(result);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load students');
    } finally {
      setLoading(false);
    }
    // filters.search is intentionally excluded — debouncedSearch stands in for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.rank, filters.instructor, filters.classId, filters.division,
    filters.gender, filters.status, filters.portalStatus, sort, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getDirectoryFilters()
      .then(({ data: result }) => setOptions(result))
      .catch(() => { /* the list still works without dropdowns */ });
  }, []);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, value]) => value).length,
    [filters]
  );

  const handleBulkInvite = async () => {
    setInviting(true);
    setInviteResult(null);
    try {
      const { data: result } = await sendBulkParentInvites();
      setInviteResult(result);
      load();
    } catch (bulkError) {
      setError(bulkError.response?.data?.error || 'Could not send invitations');
    } finally {
      setInviting(false);
    }
  };

  const summary = data.summary;
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <button
          onClick={() => setAdding(true)}
          className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-purple-700"
        >
          + Add a student
        </button>
        <button
          onClick={() => setImporting(true)}
          className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
        >
          Import parent emails
        </button>
      </div>

      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { label: 'Students', value: summary.total, tone: 'text-gray-900' },
            { label: 'No parent email', value: summary.missingParentEmail, tone: 'text-amber-600' },
            { label: 'No date of birth', value: summary.missingDateOfBirth, tone: 'text-amber-600' },
            { label: 'Signed in', value: summary.activated, tone: 'text-green-600' },
          ].map((tile) => (
            <div key={tile.label} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className={`text-2xl font-semibold ${tile.tone}`}>{tile.value}</div>
              <div className="text-xs text-gray-500">{tile.label}</div>
            </div>
          ))}
        </div>
      )}

      {summary && summary.notInvited > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
          <div className="text-sm text-purple-900">
            <strong>{summary.notInvited}</strong> {summary.notInvited === 1 ? 'family has' : 'families have'} an
            email on file but no invitation yet.
          </div>
          <button
            onClick={handleBulkInvite}
            disabled={inviting}
            className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {inviting ? 'Sending…' : 'Invite them all'}
          </button>
        </div>
      )}

      {inviteResult && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3 text-sm">
          <p className="text-gray-800">
            Sent {inviteResult.sent} {inviteResult.sent === 1 ? 'invitation' : 'invitations'}.
            {inviteResult.skipped?.length > 0 && ` Skipped ${inviteResult.skipped.length} already invited or signed in.`}
          </p>
          {inviteResult.failed?.length > 0 && (
            <p className="mt-1 text-red-600">
              {inviteResult.failed.length} could not be delivered: {inviteResult.failed.slice(0, 3).map((f) => f.email).join(', ')}
              {inviteResult.failed.length > 3 && '…'}
            </p>
          )}
        </div>
      )}

      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder="Search a name or a parent's email…"
            className={`${FIELD} min-w-[240px] flex-1`}
          />
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={FIELD} aria-label="Sort">
            <option value="name">Sort: name</option>
            <option value="rank">Sort: highest rank</option>
            <option value="age">Sort: youngest</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Select label="Rank" value={filters.rank} onChange={(v) => setFilter('rank', v)} options={options.ranks} allLabel="Any rank" />
          <Select label="Division" value={filters.division} onChange={(v) => setFilter('division', v)} options={options.divisions} allLabel="Any division" />
          <Select label="Gender" value={filters.gender} onChange={(v) => setFilter('gender', v)} options={options.genders} allLabel="Any gender" />
          <Select label="Instructor" value={filters.instructor} onChange={(v) => setFilter('instructor', v)} options={options.instructors} allLabel="Any instructor" />
          <Select label="Status" value={filters.status} onChange={(v) => setFilter('status', v)} options={options.statuses} allLabel="Any status" />
          <Select
            label="Portal"
            value={filters.portalStatus}
            onChange={(v) => setFilter('portalStatus', v)}
            options={[
              { id: 'no-email', label: 'No parent email' },
              { id: 'not-invited', label: 'Not invited' },
              { id: 'invited', label: 'Invited' },
              { id: 'activated', label: 'Signed in' },
            ]}
            allLabel="Any portal status"
          />
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {loading ? 'Loading…' : `${data.total} ${data.total === 1 ? 'student' : 'students'}`}
            {activeFilters > 0 && ' matching'}
          </span>
          {activeFilters > 0 && (
            <button
              onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}
              className="text-purple-600 hover:text-purple-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="space-y-2">
        {data.students.map((student) => (
          <StudentRow key={student.id} student={student} onOpen={setSelected} />
        ))}
      </div>

      {!loading && data.students.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center">
          <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-400">?</div>
          <p className="text-sm text-gray-500">No students match these filters.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {adding && (
        <AddStudent
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); load(); }}
        />
      )}

      {importing && (
        <EmailImport
          onClose={() => setImporting(false)}
          onImported={load}
        />
      )}

      <StudentEditor
        student={selected}
        onClose={() => setSelected(null)}
        onSaved={(updated) => {
          // Keep the row in step with the drawer without refetching 450 students.
          setSelected((current) => (current ? { ...current, ...updated } : current));
          setData((current) => ({
            ...current,
            students: current.students.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
          }));
        }}
      />
    </div>
  );
}
